/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 * @description DetailPageContext
 */

import type { BoxProps } from '@chakra-ui/react'
import type { ButtonProps } from '@zcode-ui/controls'
import type { BuildingListingIdParams } from '@zcode-ui/web-zumper-features'
import type {
  MediaDefaultIconButtonLoaderProps,
  MediaDefaultIconButtonProps,
  MediaDefaultIconLinkProps,
  MediaPictureButtonProps,
  MixpanelTracking,
  ScrollingSectionGallery,
} from '@zcode-ui/web-zumper-ui'
import type {
  AutocompleteResult,
  Media,
  MediaIframe,
  MediaImageLayout,
  MediaReel,
  MediaVideo,
  Unit,
} from '@zcode/bizos'
import type { ImageProps } from '@zcode/image'
import type { DETAIL } from '@zcode/mixpanel'
import type { Origin } from '@zcode/nr-utils'
import type {
  Building,
  Dispatch,
  FiltersState,
  FloorplanListing,
  FloorplanModalOpener,
  GetState,
  Listing,
  NormalFloorplanListingData,
  NormalListingData,
  Property,
} from '@zcode/state-type'
import type { TitleCase } from '@zcode/utils'
import type { Location, LocationDescriptor, LocationDescriptorObject } from 'history'
import React, { createContext, useContext } from 'react'
import invariant from 'tiny-invariant'

export interface UseMediaProps {
  isMobile: boolean
  media: readonly Media[]
  propertyTitle: string
}

export type MediaObjImageGalleryAndSlidePartial = Partial<MediaPictureButtonProps> & {
  imgProps: Omit<ImageProps, 'onClick' | 'sizes' | 'src' | 'srcset'>
  index: number
  /** max viewport width at which we should preload the image */
  mediaMaxWidth?: `${number}${'px' | 'vw'}`
  mediaMinWidth: `${number}px`
  sizes: string
  sources: ReadonlyArray<{
    dpr: 1 | 2 | 3
    srcSet: string
  }>
}
export interface MediaObjImage {
  isPlaceholder?: never
  media: Pick<
    MediaImageLayout,
    'id' | 'isIframe' | 'isImage' | 'isVideo' | 'mediaId' | 'url' | 'isReel'
  >
  GalleryComponent: React.ComponentType<MediaObjImageGalleryAndSlidePartial>
  SlideComponent: React.FC<MediaObjImageGalleryAndSlidePartial>
  originalIndex: number
}

export interface MediaObjPlaceholder {
  isPlaceholder: true
  media: Pick<MediaImageLayout, 'isIframe' | 'isImage' | 'url' | 'isReel'>
  GalleryComponent: React.ComponentType<React.ImgHTMLAttributes<HTMLImageElement>>
  SlideComponent: React.FC<React.ImgHTMLAttributes<HTMLImageElement>>
  originalIndex: number
}

export type MediaObjVideoGalleryPartial = Partial<MediaDefaultIconLinkProps> &
  Pick<MediaDefaultIconLinkProps, 'height' | 'width'>
export type MediaObjVideoSlidePartial = Partial<MediaDefaultIconButtonLoaderProps> &
  Pick<MediaDefaultIconButtonLoaderProps, 'height' | 'width'>
export interface MediaObjVideo {
  isPlaceholder?: never
  media: Pick<MediaVideo, 'id' | 'isIframe' | 'isImage' | 'isVideo' | 'mediaId' | 'url' | 'isReel'>
  GalleryComponent: React.ComponentType<MediaObjVideoGalleryPartial>
  SlideComponent: React.FC<MediaObjVideoSlidePartial>
  originalIndex: number
}

export type MediaObjIframeGalleryPartial = Partial<MediaDefaultIconLinkProps> &
  Pick<MediaDefaultIconLinkProps, 'height' | 'width'>
export type MediaObjIframeSlidePartial = Partial<MediaDefaultIconButtonLoaderProps> &
  Pick<MediaDefaultIconButtonLoaderProps, 'height' | 'width'>
export interface MediaObjIframe {
  isPlaceholder?: never
  media: Pick<MediaIframe, 'id' | 'isIframe' | 'isImage' | 'isVideo' | 'mediaId' | 'url' | 'isReel'>
  GalleryComponent: React.ComponentType<MediaObjIframeGalleryPartial>
  SlideComponent: React.FC<MediaObjIframeSlidePartial>
  originalIndex: number
}

export type MediaObjReelGalleryPartial = Partial<MediaDefaultIconButtonProps> &
  Pick<MediaDefaultIconButtonProps, 'height' | 'width'>
export type MediaObjReelSlidePartial = Partial<MediaDefaultIconButtonLoaderProps> &
  Pick<MediaDefaultIconButtonLoaderProps, 'height' | 'width'>
export interface MediaObjReel {
  isPlaceholder?: never
  media: Pick<MediaReel, 'id' | 'isIframe' | 'isImage' | 'isVideo' | 'isReel' | 'mediaId' | 'url'>
  GalleryComponent: React.ComponentType<MediaObjReelGalleryPartial>
  SlideComponent: React.FC<MediaObjReelSlidePartial>
  originalIndex: number
}

export type MediaObj = MediaObjImage | MediaObjVideo | MediaObjIframe | MediaObjReel
export type MediaObjOrPlaceholderArray = ReadonlyArray<MediaObjPlaceholder | MediaObj>

export type DetailAdsLayoutTypeUnion =
  | {
      layout: 'square' | 'inactiveSquare' | 'topSquare'
      adType?: 1 | 2
    }
  | {
      layout: 'gallerySquare' | 'gallerySticky' | 'inactiveSticky' | 'leaderboard' | 'twoSquare'
      adType?: undefined
    }

export interface DetailPageContextType {
  useViewDetailPageSection: (
    sectionName:
      | undefined
      | (typeof DETAIL)[keyof typeof DETAIL]
      | [
          mixpanelSectionName: (typeof DETAIL)[keyof typeof DETAIL],
          traktorEventName: `${string}_IMPRESSION`,
        ]
  ) => ((ref: HTMLElement | null) => void) | undefined
  AlertMeButton: React.FunctionComponent<{
    origin:
      | typeof Origin.DETAIL_TOP_SAVE_SEARCH
      | typeof Origin.ADDR_DETAIL_TOP_SAVE_SEARCH
      | typeof Origin.DETAIL_MIDDLE_SAVE_SEARCH
    hasTooltip?: boolean
    children?: React.ReactNode
  }>
  DetailAds: React.FunctionComponent<
    Pick<BoxProps, 'marginBottom' | 'marginTop'> & {
      showBorderBottom?: boolean
      showBorderTop?: boolean
    } & DetailAdsLayoutTypeUnion
  >
  DetailAppBanner: React.FunctionComponent<{}>
  FavoriteContainer: React.FC<{
    className?: string
    entity: Property | null | undefined // TODO: can we get undefined out of here?
    isRoundButton?: boolean
    shouldUseTransparentBg?: boolean
    size?: number
    stroke?: string
    borderStyle?: 'none' | 'solid'
    text?: React.ReactNode
  }>
  DetailFlagButtonContainer: React.ComponentType<{ entity: Building | Listing<NormalListingData> }>
  handleBackBtnClick: (result: AutocompleteResult) => void
  getRootRelativeUrlFromFiltersAndLocation: ({
    bubbleId,
    currentUrl,
    filters,
    locationUrlQueries,
    omitBox,
  }: {
    bubbleId?: string | undefined
    currentUrl: string
    filters: FiltersState
    locationUrlQueries?: string[] | undefined
    omitBox?: boolean | undefined
  }) => string
  getImagePlaceholder: (groupId: number) => string
  keepBetaParam: <ToLocationState>(
    to: LocationDescriptor<ToLocationState>
  ) => (location: Location<unknown>) => LocationDescriptor<ToLocationState>
  MessageButton: React.ForwardRefExoticComponent<
    React.RefAttributes<HTMLAnchorElement & HTMLButtonElement> &
      Pick<ButtonProps<undefined>, 'className' | 'size' | 'variant'> & {
        disableOneTap?: boolean
        entity: Property | null | undefined
        equalCTAIntent?: 'Apply' | 'Message' | 'Waitlist'
        hiddenText?: string
        isQuestionModal?: boolean
        onlyShowFloorplanGroup?: boolean
        origin: Origin
        selectedFloorplan?: FloorplanListing<NormalFloorplanListingData>
        showAgentInfo?: boolean
        text?: string
      }
  >
  NotifyMeButton: React.FC<{
    applyTopBarStyle?: boolean
    selectedFloorplan?: FloorplanListing<NormalFloorplanListingData>
    origin:
      | typeof Origin.DETAIL_TOP_RIGHT_RAIL
      | typeof Origin.ADDR_DETAIL_TOP_RIGHT_RAIL
      | typeof Origin.DETAIL_NAV_STICKY
      | typeof Origin.ADDR_DETAIL_NAV_STICKY
      | typeof Origin.DETAIL_MOBILE_STICKY
      | typeof Origin.ADDR_DETAIL_MOBILE_STICKY
      | typeof Origin.FLOORPLAN
      | typeof Origin.MOBILE_FLOORPLAN
  }>
  InactiveSaveSearchSection: React.FC<{
    applyTopBarStyle?: boolean
    origin: typeof Origin.DETAIL_TOP_RIGHT_RAIL | typeof Origin.ADDR_DETAIL_TOP_RIGHT_RAIL
  }>
  TourButton: React.FC<{
    buttonProps?: Pick<ButtonProps<undefined>, 'className' | 'size' | 'variant'>
    entity: Property | null | undefined
    origin: Origin
    selectedFloorplan?: FloorplanListing<NormalFloorplanListingData>
    selectedUnit?: Unit
    children?: React.ReactNode
  }> & { defaultProps?: never; propTypes?: never }
  mediaBarTracking: {
    trackOnImagesClick: () => void
    trackOnVideosClick: () => void
    trackOnToursClick: () => void
    trackOnViewAllClick: () => void
    trackOnReelsClick: () => void
  }
  Navbar: React.FC<{ isBuilding: boolean | undefined; isStatic: boolean }> // TODO: can we get undefined out of here?
  openFloorplanDetailModal: (payload: {
    opener: FloorplanModalOpener
    selectedFloorplan: number
  }) => void
  preloadRoutes: (componentNames: string[]) => void
  Routes: {
    CURRENT_SEARCH_ROUTE: string
  }
  SaveSearchButton: React.FunctionComponent
  TourBookingContainer: React.FC<{
    analyticChannel: 'Detail'
    origin: typeof Origin.ADDR_DETAIL_TOP_RIGHT_TOUR | typeof Origin.DETAIL_TOP_RIGHT_TOUR
  }>
  AnalyticsConsts: { FULL_DETAIL: 'Detail' }
  selectDisplayMedia: (
    entity: Pick<Property, 'media'> | { media: undefined } | undefined | null,
    noSort?: boolean
  ) => Media[] | null
  selectGalleryUrl: (
    section?: ScrollingSectionGallery | Pick<Media, 'id'>
  ) => (
    location: Location<unknown>
  ) => LocationDescriptorObject<{ previousLocation: Location<unknown> }>
  selectFocusedGalleryUrl: (
    index: number
  ) => (
    location: Location<unknown>
  ) => LocationDescriptorObject<{ previousLocation: Location<unknown> }>
  selectHasRoomTypeFloorplans: typeof import(
    '@zcode/nr/es/shared/bizos/rentable/selectors'
  ).selectHasRoomTypeFloorplans
  selectIsMessaged: (
    entity: Pick<Property, 'buildingId' | 'listingId' | 'pbId' | 'plId'> | null | undefined
  ) => boolean
  shouldLimitLeads: boolean
  showAIAssistant: boolean
  EngrainPropertyMap: React.FC<{
    building: Building
    isOpen: boolean
    onOpen: () => void
    onClose: () => void
  }>
  MixpanelThunks: MixpanelTracking
  trackCall: (props: {
    entity: Property | null | undefined
    origin:
      | typeof Origin.DETAIL_TOP_RIGHT_RAIL
      | typeof Origin.DETAIL_MOBILE_STICKY
      | typeof Origin.ADDR_DETAIL_MOBILE_STICKY
  }) => void
  trackFloorplanEvent: (
    action: 'Show Floorplan Summary',
    entity: Property | null | undefined,
    label:
      | TitleCase<Exclude<FloorplanListing['bedText'], ''> | 'room'>
      | Exclude<FloorplanListing['title'], undefined>
  ) => void
  traktorEvent: (props: { name: string }) => void
  trackUnloadableTour: (trackingParams: {
    entity: Property | null | undefined
    mediaId?: number | undefined
    mediaUrl?: string | null
    origin: Origin
    previewUrl?: string | null
  }) => void
  trackFloorplanView: (listingId: number) => void
  trackAIAssistantEntryClick: ({
    additionalProps,
  }: {
    additionalProps: { placement: string; entry: string }
  }) => (dispatch: Dispatch, getState: GetState) => void
  trackAIAssistantEntryImpression: ({
    additionalProps,
  }: {
    additionalProps: { placement: string }
  }) => (dispatch: Dispatch, getState: GetState) => void
  useStreamChannel: (props: BuildingListingIdParams) => {
    loading: boolean
    streamChannelId: string | null | undefined
  }
  urlParamListingId: string | string[] | undefined
  searchLinks: ReadonlyArray<{
    label: string
    url:
      | `/${'apartments' | 'houses' | 'condos' | 'rooms'}-for-rent/${string}`
      | `/${`${'apartments' | 'houses' | 'condos' | 'rooms'}-for-rent` | 'rentals'}/${string}/${string}`
  }>
}

export const DetailPageContext = createContext<DetailPageContextType | null>(null)

export const useDetailPageContext = () => {
  const context = useContext(DetailPageContext)
  invariant(context)
  return context
}

export const useIsNewDetailPage = () => {
  const context = useContext(DetailPageContext)
  return !!context
}
